import * as React from 'react';
import propz from 'propz';
import { LIMIT } from '../../../../consts/table';
import { formatAgesCompact, resolveAgeNamingFromContext } from '../../../../helpers/ageGroups';
import { getClubs } from '../../../../helpers/service/admin/clubs';
import { getClubStudentsCount } from '../../../../helpers/service/admin/clubStudents';
import { deleteProgrammeClub, updateProgrammeClub } from '../../../../helpers/service/admin/clubProgrammes';
import {
  getErrMsg,
  fmtDMY,
  getClubDay,
  getClubSportName,
  getClubStartIso,
  getClubFinishIso,
  isOneDayClub,
  isStartedOrPast,
  isFinishedOrPast,
  isClubRemoved
} from '../../../../helpers/clubProgramme/clubProgramme';
import { useHistory, useLocation } from 'react-router-dom';
import { AfterSchoolClubProgramme } from '../../../../models/clubProgramme';
import { ClubProgrammeBadge } from '../../../../types/clubProgramme';
import { AppUser } from '../../../../views/App/App';
import { ClubProgrammeManageClubsInviteModal } from './components/ClubProgrammeManageClubsInviteModal';
import { ClubProgrammeManageClubsTable, ManageClubColumn } from './components/ClubProgrammeManageClubsTable';
import { ClubProgrammeManageClubsRemoveSelectedModal } from './components/ClubProgrammeManageClubsRemoveSelectedModal';
import { ClubProgrammeAddClubsModal } from './components/ClubProgrammeAddClubsModal';
import { getProgrammeClubDayShortLabel, getProgrammeClubGenderLabel } from './helpers/clubProgrammeClubsContent';
import { programmeBookingFormatTimeRange } from '../../../../helpers/clubProgramme/clubProgrammeBooking';
import { useClubProgrammeManageClubsInvite } from './hooks/useClubProgrammeManageClubsInvite';
import { getFormFieldTypeDefinition } from '../../../../helpers/form/formFieldRegistry';

type ManageClubsIconName =
  | 'back'
  | 'search'
  | 'whistle'
  | 'calendar'
  | 'lock'
  | 'list'
  | 'mail'
  | 'filter'
  | 'columns'
  | 'plus'
  | 'edit'
  | 'swap'
  | 'info';

const MANAGE_CLUBS_ICON_PATHS: Record<ManageClubsIconName, string> = {
  back: 'M19 12H5M12 19l-7-7 7-7',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.35-4.35',
  whistle: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  lock: 'M6 11V8a6 6 0 0 1 12 0v3M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  mail: 'M4 4h16v16H4zM4 7l8 6 8-6',
  filter: 'M3 5h18M6 12h12M10 19h4',
  columns: 'M4 5h16v14H4zM9 5v14M15 5v14',
  plus: 'M12 5v14M5 12h14',
  edit: 'M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z',
  swap: 'M16 3l4 4-4 4M20 7H8M8 21l-4-4 4-4M4 17h12',
  info: 'M12 16v-4M12 8h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20'
};

const ManageClubsIcon: React.FC<{ icon: ManageClubsIconName; size?: number; strokeWidth?: number }> = ({
  icon,
  size = 16,
  strokeWidth = 1.8
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={MANAGE_CLUBS_ICON_PATHS[icon]} />
  </svg>
);

const MANAGE_CLUB_COLUMNS: ManageClubColumn[] = [
  { field: 'club', text: 'Club', width: 280 },
  { field: 'dateTime', text: 'Date/Time', width: 150 },
  { field: 'audience', text: 'Audience', width: 150 },
  { field: 'places', text: 'Places', width: 90 },
  { field: 'bookingForm', text: 'Booking form', width: 150 },
  { field: 'invites', text: 'Invites', width: 150 },
  { field: 'status', text: 'Status', width: 112 }
];

const MANAGE_CLUB_LOCKED_COLUMNS = new Set(['club']);

const getManageColumnsStorageKey = (user: AppUser): string => {
  const userId = String(
    propz.get(user, ['id'], '') || propz.get(user, ['_id'], '') || propz.get(user, ['email'], '') || 'anonymous'
  );
  return `columnVisibility__${userId}__club-programme-manage-clubs`;
};

const readHiddenManageColumns = (storageKey: string): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    return Array.isArray(stored) ? stored.filter(field => typeof field === 'string') : [];
  } catch (_error) {
    return [];
  }
};

type Props = {
  user: AppUser;
  programme: AfterSchoolClubProgramme;
  onBackClick: () => void;
  onProgrammeUpdated: (p: AfterSchoolClubProgramme) => void;
};

export const ClubProgrammeManageClubsContent: React.FC<Props> = ({
  user,
  programme,
  onBackClick,
  onProgrammeUpdated
}) => {
  const history = useHistory();
  const location = useLocation<any>();
  const [manageErr, setManageErr] = React.useState<string>('');
  const clearManageErr = () => setManageErr('');
  const [isAddClubsOpen, setIsAddClubsOpen] = React.useState(() => !!location?.state?.openAddClubs);

  const canEdit = String((programme as any).status || 'DRAFT') === 'DRAFT';
  const programmeId = String((programme as any).id || (programme as any)._id || '');

  const programmeName = String((programme as any).name || 'Programme');

  const naming = React.useMemo(() => resolveAgeNamingFromContext(user, programme), [user, programme]);
  const getClubAgesLabel = (c: any) => formatAgesCompact(propz.get(c, ['ages'], []), naming);

  const programmeClubs: any[] = programme && (programme as any).clubs ? ((programme as any).clubs as any[]) || [] : [];

  const programmeClubIds = React.useMemo(() => {
    return (programmeClubs || []).map((pc: any) => String(pc && pc.clubId)).filter(Boolean);
  }, [programmeClubs]);

  const [programmeClubDetailsById, setProgrammeClubDetailsById] = React.useState<{ [clubId: string]: any }>({});
  const [isProgrammeClubsLoading, setIsProgrammeClubsLoading] = React.useState(false);

  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);
  const [selectedProgrammeClubIds, setSelectedProgrammeClubIds] = React.useState<string[]>([]);
  const [previewProgrammeClubId, setPreviewProgrammeClubId] = React.useState<string>('');

  const onTabClick = (event, index: number) => {
    event.preventDefault();
    setCurrentTabIndex(index);
  };

  const refreshProgrammeClubDetails = React.useCallback(
    (idsOverride?: string[]) => {
      const ids = (idsOverride && idsOverride.length ? idsOverride : programmeClubIds) || [];

      if (!ids.length) {
        setProgrammeClubDetailsById({});
        return;
      }

      setIsProgrammeClubsLoading(true);
      setManageErr('');

      getClubs(user, {
        limit: 5000,
        skip: 0,
        where: { _id: { $in: ids } },
        sort: 'name ASC'
      } as any)
        .then((items: any[]) => {
          const map: any = {};
          (items || []).forEach(c => {
            const id = String(c && (c.id || c._id));
            if (id) map[id] = c;
          });
          setProgrammeClubDetailsById(map);
          setIsProgrammeClubsLoading(false);
        })
        .catch(e => {
          setProgrammeClubDetailsById({});
          setIsProgrammeClubsLoading(false);
          setManageErr(getErrMsg(e, 'Failed to load programme clubs'));
        });
    },
    [user, programmeClubIds.join('|')]
  );

  React.useEffect(() => {
    refreshProgrammeClubDetails();
  }, [refreshProgrammeClubDetails]);

  React.useEffect(() => {
    if (location?.state?.openAddClubs) setIsAddClubsOpen(true);
  }, [location?.state?.openAddClubs]);

  const getClubBadges = React.useCallback((club: any): ClubProgrammeBadge[] => {
    if (!club) return [{ kind: 'warn', text: 'Not found' }];

    const badges: ClubProgrammeBadge[] = [];
    const removed = isClubRemoved(club);
    const status = String(club.status || '');

    if (removed) badges.push({ kind: 'danger', text: 'Deleted' });
    if (isStartedOrPast(club)) badges.push({ kind: 'warn', text: 'Started' });
    if (!isOneDayClub(club)) badges.push({ kind: 'info', text: 'Multiple days' });
    if (status && status !== 'DRAFT') badges.push({ kind: 'info', text: `Status: ${status}` });
    if (isFinishedOrPast(club)) badges.push({ kind: 'warn', text: 'No longer eligible' });

    if (badges.length === 0) badges.push({ kind: 'ok', text: 'Eligible' });
    return badges;
  }, []);

  const [savingIds, setSavingIds] = React.useState<{ [k: string]: boolean }>({});
  const setSaving = (programmeClubId: string, v: boolean) => {
    setSavingIds(prev => ({ ...prev, [String(programmeClubId)]: v }));
  };

  const ageGroupsNaming = propz.get(user, ['activeSchool', 'ageGroupsNaming'], undefined);

  const {
    isInviteListOpen,
    inviteInvitesOnly,
    inviteErr,
    inviteEligibleLoading,
    inviteEligibleSelected,
    setInviteEligibleSelected,
    inviteInvitedItems,
    inviteInvitedItemsSorted,
    inviteInvitedSelected,
    setInviteInvitedSelected,
    inviteFiltersDraft,
    invitePresetFilters,
    setInviteFiltersDraft,
    inviteItemsFiltersOptions,
    inviteShowEligibleFilter,
    setInviteShowEligibleFilter,
    inviteTotal,
    invitePage,
    setInvitePage,
    setInviteFiltersApplied,
    replaceInviteFilters,
    inviteSearch,
    setInviteSearch,
    inviteEligibleItems,
    inviteItemsAddBuffer,
    inviteItemsRemoveBuffer,
    openInviteList,
    closeInviteList,
    onInviteListSave,
    loadInvitePage,
    onInviteAddClick,
    onInviteRemoveClick,
    onInviteAddSingleItem,
    onInviteRemoveSingleItem,
    INVITE_FILTERS_EMPTY,
    INVITE_ITEMS_COLUMNS,
    inviteClubName,
    loadAllInviteEligible,
    setInviteEligibleLoading,
    inviteInitialInvitedIds,
    inviteInvitedLoading
  } = useClubProgrammeManageClubsInvite({
    user,
    programmeId,
    ageGroupsNaming,
    onProgrammeUpdated,
    setSaving,
    updateProgrammeClub
  });

  const onToggleEnabled = (programmeClubId: string, isEnabled: boolean) => {
    setManageErr('');
    setSaving(programmeClubId, true);

    updateProgrammeClub(user, programmeId, programmeClubId, { isEnabled })
      .then(updated => {
        onProgrammeUpdated(updated);
        setSaving(programmeClubId, false);
      })
      .catch(e => {
        setManageErr(getErrMsg(e, 'Failed to update programme club'));
        setSaving(programmeClubId, false);
      });
  };

  const onToggleInvitesOnly = (programmeClubId: string, invitesOnly: boolean) => {
    setManageErr('');
    setSaving(programmeClubId, true);

    updateProgrammeClub(user, programmeId, programmeClubId, { invitesOnly })
      .then(updated => {
        onProgrammeUpdated(updated);
        setSaving(programmeClubId, false);
      })
      .catch(e => {
        setManageErr(getErrMsg(e, 'Failed to update programme club'));
        setSaving(programmeClubId, false);
      });
  };

  const onSetBookingFormMode = (programmeClubId: string, mode: 'DEFAULT' | 'CUSTOM') => {
    if (!programmeClubId || !programmeId) return;
    setManageErr('');
    setSaving(programmeClubId, true);

    const emptyCustomDefinition = {
      schemaVersion: 2,
      title: 'Custom club booking form',
      description: '',
      elements: [],
      logic: []
    };

    const payload =
      mode === 'DEFAULT'
        ? ({ fieldsOverride: null } as any)
        : ({
            fieldsOverride: {
              mode: 'REPLACE',
              formSchemaVersion: 2,
              formDefinition: emptyCustomDefinition,
              fields: []
            }
          } as any);

    updateProgrammeClub(user, programmeId, programmeClubId, payload)
      .then(updated => {
        onProgrammeUpdated(updated);
        setSaving(programmeClubId, false);
      })
      .catch(e => {
        setManageErr(getErrMsg(e, 'Failed to update booking form mode'));
        setSaving(programmeClubId, false);
      });
  };
  const onDelete = (programmeClubId: string) => {
    setManageErr('');
    setSaving(programmeClubId, true);

    deleteProgrammeClub(user, programmeId, programmeClubId)
      .then(updated => {
        onProgrammeUpdated(updated);
        setSaving(programmeClubId, false);
        refreshProgrammeClubDetails();
      })
      .catch(e => {
        setManageErr(getErrMsg(e, 'Failed to remove club from programme'));
        setSaving(programmeClubId, false);
      });
  };

  const [isManageFiltersOpen, setIsManageFiltersOpen] = React.useState(false);
  const [isManageColumnsOpen, setIsManageColumnsOpen] = React.useState(false);
  const manageColumnsStorageKey = React.useMemo(() => getManageColumnsStorageKey(user), [user]);
  const manageColumnsRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!isManageColumnsOpen) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (manageColumnsRef.current && !manageColumnsRef.current.contains(event.target as Node)) {
        setIsManageColumnsOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isManageColumnsOpen]);
  const [hiddenManageColumnKeys, setHiddenManageColumnKeys] = React.useState<string[]>(() =>
    readHiddenManageColumns(manageColumnsStorageKey)
  );
  const [manageSearch, setManageSearch] = React.useState('');
  const [appliedManageSearch, setAppliedManageSearch] = React.useState('');
  const [managePage, setManagePage] = React.useState(1);
  const [manageOnlyDisabled, setManageOnlyDisabled] = React.useState(false);
  const [manageOnlyNoLongerEligible, setManageOnlyNoLongerEligible] = React.useState(false);
  const [manageOnlyDisabledDraft, setManageOnlyDisabledDraft] = React.useState(false);
  const [manageOnlyNoLongerEligibleDraft, setManageOnlyNoLongerEligibleDraft] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(manageColumnsStorageKey, JSON.stringify(hiddenManageColumnKeys));
    } catch (_error) {
      // Column preferences are a progressive enhancement; the table remains usable without storage.
    }
  }, [hiddenManageColumnKeys, manageColumnsStorageKey]);

  React.useEffect(() => {
    if (!isManageFiltersOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsManageFiltersOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isManageFiltersOpen]);

  const [clubStudentsCountByClubId, setClubStudentsCountByClubId] = React.useState<{ [clubId: string]: number }>({});
  const [clubStudentsCountLoadingByClubId, setClubStudentsCountLoadingByClubId] = React.useState<{
    [clubId: string]: boolean;
  }>({});

  const [isBulkDisabling, setIsBulkDisabling] = React.useState(false);
  const [bulkProgress, setBulkProgress] = React.useState<{ done: number; total: number }>({ done: 0, total: 0 });

  const [manageAllSelected, setManageAllSelected] = React.useState(false);
  const [manageSelectedIds, setManageSelectedIds] = React.useState<{ [id: string]: boolean }>({});
  const [manageDeselectedIds, setManageDeselectedIds] = React.useState<{ [id: string]: boolean }>({});
  const [manageAllSelectedWhere, setManageAllSelectedWhere] = React.useState<any>(null);

  const [isRemoveSelectedOpen, setIsRemoveSelectedOpen] = React.useState(false);

  const openFieldsOverride = (row: any) => {
    const pcId = String(row?.programmeClubId || '');
    if (!pcId || !programmeId) return;

    history.push({
      pathname: `/clubProgrammes/${programmeId}/manageclubs/${pcId}/fields`,
      state: {
        from: {
          pathname: `/clubProgrammes/${programmeId}/manageclubs`,
          search: location.search || ''
        }
      }
    });
  };

  const manageClearSelection = React.useCallback(() => {
    setManageAllSelected(false);
    setManageSelectedIds({});
    setManageDeselectedIds({});
    setManageAllSelectedWhere(null);
    setSelectedProgrammeClubIds([]);
  }, []);

  const onAddClubsProgrammeUpdated = React.useCallback(
    (updated: AfterSchoolClubProgramme) => {
      onProgrammeUpdated(updated);
      const updatedClubIds = (((updated as any)?.clubs || []) as any[])
        .map((pc: any) => String(pc && pc.clubId))
        .filter(Boolean);
      refreshProgrammeClubDetails(updatedClubIds);
      manageClearSelection();
    },
    [onProgrammeUpdated, refreshProgrammeClubDetails, manageClearSelection]
  );

  React.useEffect(() => {
    setManagePage(1);
  }, [appliedManageSearch, manageOnlyDisabled, manageOnlyNoLongerEligible]);

  React.useEffect(() => {
    manageClearSelection();
  }, [appliedManageSearch, manageOnlyDisabled, manageOnlyNoLongerEligible, manageClearSelection]);

  const countFormQuestions = (definition: any): number =>
    Array.isArray(definition?.elements)
      ? definition.elements.filter((element: any) => element?.kind === 'QUESTION').length
      : 0;
  const mapLegacyFieldToFormElement = (field: any, index: number) => {
    const legacyType = String(field?.type || 'STRING').toUpperCase();
    const type =
      legacyType === 'BOOLEAN'
        ? 'YES_NO'
        : legacyType === 'ENUM'
        ? 'SINGLE_CHOICE'
        : legacyType === 'NUMBER'
        ? 'NUMBER'
        : 'SHORT_TEXT';

    return {
      id: String(field?.id || field?._id || `legacy-${index}`),
      kind: 'QUESTION',
      type,
      title: String(
        field?.heading || field?.title || field?.name || getFormFieldTypeDefinition(type as any)?.label || 'Question'
      ),
      required: !!field?.isRequired
    };
  };

  const getFormElementsFromDefinition = (definition: any, legacyFields: any[] = []) => {
    if (Array.isArray(definition?.elements)) return definition.elements;
    return Array.isArray(legacyFields) ? legacyFields.map(mapLegacyFieldToFormElement) : [];
  };

  const getBookingFormPreview = (row: any) => {
    const fieldsOverride = row?.pc?.fieldsOverride;
    const overrideMode = String(fieldsOverride?.mode || 'REPLACE');
    const defaultFields = Array.isArray((programme as any)?.defaultFields) ? (programme as any).defaultFields : [];
    const overrideFields = Array.isArray(fieldsOverride?.fields) ? fieldsOverride.fields : [];
    const defaultElements = getFormElementsFromDefinition((programme as any)?.formDefinition, defaultFields);
    const overrideElements = getFormElementsFromDefinition(fieldsOverride?.formDefinition, overrideFields);
    const hasOverride = !!fieldsOverride;
    const customNotSetup = hasOverride && overrideElements.length === 0;
    const elements = !hasOverride
      ? defaultElements
      : customNotSetup
      ? []
      : overrideMode === 'MERGE'
      ? [...defaultElements, ...overrideElements]
      : overrideElements;
    const questionElements = elements.filter((element: any) => element?.kind === 'QUESTION');
    const required = questionElements.filter((element: any) => !!element?.required || !!element?.isRequired).length;
    const pages = Math.max(1, elements.filter((element: any) => element?.type === 'PAGE_BREAK').length + 1);

    return {
      hasOverride,
      customNotSetup,
      modeLabel: hasOverride ? 'Custom for this club' : 'Programme default',
      sourceLabel: hasOverride ? 'Custom form' : 'Programme default form',
      elements,
      questions: questionElements.length,
      required,
      pages
    };
  };
  const manageList = React.useMemo(() => {
    const q = (appliedManageSearch || '').trim().toLowerCase();

    const programmeDefaultFields = Array.isArray((programme as any)?.defaultFields)
      ? (programme as any).defaultFields
      : [];
    const programmeQuestionCount =
      countFormQuestions((programme as any)?.formDefinition) || programmeDefaultFields.length;

    const rows = (programmeClubs || []).map(pc => {
      const clubId = String(pc && pc.clubId);
      const club = programmeClubDetailsById[clubId];

      const isClubDetailsLoaded = !!club;
      const name = isClubDetailsLoaded ? String(propz.get(club, ['name'], '') || '') : '';

      const sportName = getClubSportName(club);
      const day = getProgrammeClubDayShortLabel(getClubDay(club));
      const startIso = getClubStartIso(club);
      const finishIso = getClubFinishIso(club);
      const startDate = fmtDMY(startIso);
      const finishDate = fmtDMY(finishIso);
      const schedule = propz.get(club, ['schedule'], {}) || {};
      const sessionTime = programmeBookingFormatTimeRange(schedule);
      const startTime = String(propz.get(schedule, ['startTime'], '') || '').trim();
      const time = startTime || (sessionTime ? String(sessionTime).split('–')[0] : '');
      const dateRange = startDate && finishDate ? `${startDate} – ${finishDate}` : startDate || finishDate;
      const gender = getProgrammeClubGenderLabel(club);
      const ages = getClubAgesLabel(club);
      const badges = getClubBadges(club);

      const isNoLongerEligible = isFinishedOrPast(club);
      const isDisabled = !(pc && pc.isEnabled);

      const invitesOnly = !!(pc && (pc as any).invitesOnly);
      const allowList = pc && (pc as any).allowList ? (pc as any).allowList : [];
      const invitedCount = Array.isArray(allowList) ? allowList.length : 0;

      const fieldsOverride = pc && (pc as any).fieldsOverride ? (pc as any).fieldsOverride : null;
      const customFields = Array.isArray(fieldsOverride?.fields) ? fieldsOverride.fields : [];
      const usesCustomFields = !!fieldsOverride;
      const overrideMode = String(fieldsOverride?.mode || 'MERGE');

      const defaultFieldsCount = programmeQuestionCount;
      const customFieldsCount = countFormQuestions(fieldsOverride?.formDefinition) || customFields.length;

      const fieldsCount = !usesCustomFields
        ? defaultFieldsCount
        : overrideMode === 'MERGE'
        ? defaultFieldsCount + customFieldsCount
        : customFieldsCount;

      const fieldsLabel = `${usesCustomFields ? 'Custom' : 'Default'} (${fieldsCount})`;

      const fieldsBadgeClass = !usesCustomFields
        ? 'pcTablePlainText'
        : fieldsCount === 0
        ? 'pcDsChip pcDsChip--warning'
        : 'pcDsChip pcDsChip--info';

      const invitesLabel = invitesOnly ? `Invites only (${invitedCount})` : 'Open';
      const invitesDisplayLabel = invitesOnly ? 'Invites only' : 'Open to eligible';
      const fieldsDisplayLabel = !usesCustomFields
        ? 'Programme default'
        : fieldsCount === 0
        ? 'Custom · Not set up'
        : `Custom · ${fieldsCount} questions`;
      const placesRaw = Number(
        propz.get(club, ['maximumNumberOfStudents'], 0) ||
          propz.get(club, ['maxParticipants'], 0) ||
          propz.get(club, ['places'], 0) ||
          0
      );
      const placesLabel = Number.isFinite(placesRaw) && placesRaw > 0 ? String(Math.trunc(placesRaw)) : 'Unlimited';
      const invitesBadgeClass = invitesOnly ? 'pcDsChip pcDsChip--info' : 'pcTablePlainText';

      return {
        pc,
        programmeClubId: String(pc && (pc._id || pc.id)),
        clubId,
        club,
        name,
        isClubDetailsLoaded,
        sportName,
        day,
        startDate,
        finishDate,
        dateRange,
        sessionTime,
        time,
        gender,
        ages,
        badges,
        isNoLongerEligible,
        isDisabled,
        invitesOnly,
        invitedCount,
        fieldsCount,
        fieldsLabel,
        fieldsBadgeClass,
        invitesLabel,
        invitesDisplayLabel,
        fieldsDisplayLabel,
        placesLabel,
        invitesBadgeClass
      };
    });

    const filtered = q
      ? rows.filter(r => {
          const hay = `${r.name} ${r.sportName} ${r.day} ${r.startDate}`.toLowerCase();
          return hay.includes(q);
        })
      : rows;

    filtered.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return filtered;
  }, [programmeClubs, programme?.defaultFields, programmeClubDetailsById, appliedManageSearch, getClubBadges]);

  const manageListFiltered = React.useMemo(() => {
    let rows = manageList;
    if (manageOnlyDisabled) rows = rows.filter(r => r.isDisabled);
    if (manageOnlyNoLongerEligible) rows = rows.filter(r => r.isNoLongerEligible);
    return rows;
  }, [manageList, manageOnlyDisabled, manageOnlyNoLongerEligible]);

  const manageTotal = manageListFiltered.length;
  const managePages = Math.max(1, Math.ceil(manageTotal / LIMIT));
  const manageSafePage = Math.min(Math.max(1, managePage), managePages);
  const manageSkip = (manageSafePage - 1) * LIMIT;
  const manageVisible = manageListFiltered.slice(manageSkip, manageSkip + LIMIT);

  const selectedProgrammeClubs = React.useMemo(() => {
    const idSet = new Set((selectedProgrammeClubIds || []).map(String));
    return (manageListFiltered || []).filter(row => idSet.has(String(row.programmeClubId)));
  }, [manageListFiltered, selectedProgrammeClubIds]);

  const selectedProgrammeClubsCount = selectedProgrammeClubs.length;

  React.useEffect(() => {
    const visibleClubIds = (manageVisible || []).map((r: any) => String(r && r.clubId)).filter(Boolean);

    const toLoad = visibleClubIds.filter(
      clubId => typeof clubStudentsCountByClubId[clubId] !== 'number' && !clubStudentsCountLoadingByClubId[clubId]
    );

    if (!toLoad.length) return;

    toLoad.forEach(clubId => {
      setClubStudentsCountLoadingByClubId(prev => ({ ...prev, [clubId]: true }));

      getClubStudentsCount(user, clubId)
        .then((cnt: any) => {
          const n = Number((cnt && (cnt as any).count) || 0);
          setClubStudentsCountByClubId(prev => ({ ...prev, [clubId]: n }));
        })
        .catch(() => {
          setClubStudentsCountByClubId(prev => ({ ...prev, [clubId]: 0 }));
        })
        .then(
          () => {
            setClubStudentsCountLoadingByClubId(prev => ({ ...prev, [clubId]: false }));
          },
          () => {
            setClubStudentsCountLoadingByClubId(prev => ({ ...prev, [clubId]: false }));
          }
        );
    });
  }, [user, manageVisible, clubStudentsCountByClubId, clubStudentsCountLoadingByClubId]);

  React.useEffect(() => {
    const visibleIds = new Set((manageListFiltered || []).map(r => String(r.programmeClubId || '')));

    const nextSelectedIds = (selectedProgrammeClubIds || []).filter(id => visibleIds.has(String(id)));
    const prevKey = (selectedProgrammeClubIds || []).map(String).join('|');
    const nextKey = nextSelectedIds.map(String).join('|');

    if (prevKey !== nextKey) {
      setSelectedProgrammeClubIds(nextSelectedIds);
    }
  }, [manageListFiltered, selectedProgrammeClubIds]);

  const selectedRow = React.useMemo(() => {
    if (!previewProgrammeClubId) return null;
    return (
      (manageListFiltered || []).find(row => String(row.programmeClubId) === String(previewProgrammeClubId)) || null
    );
  }, [manageListFiltered, previewProgrammeClubId]);

  const manageGetSelectedExplicitIds = () => Object.keys(manageSelectedIds).filter(k => !!manageSelectedIds[k]);
  const manageGetDeselectedExplicitIds = () => Object.keys(manageDeselectedIds).filter(k => !!manageDeselectedIds[k]);

  const manageSelectedCount = React.useMemo(() => {
    if (manageAllSelected) {
      const excluded = Object.keys(manageDeselectedIds).filter(k => manageDeselectedIds[k]).length;
      return Math.max(0, (manageTotal || 0) - excluded);
    }
    return manageGetSelectedExplicitIds().length;
  }, [manageAllSelected, manageDeselectedIds, manageSelectedIds, manageTotal]);

  const manageHasAnySelection = React.useMemo(() => {
    if (manageAllSelected) return (manageTotal || 0) > 0;
    return manageGetSelectedExplicitIds().length > 0;
  }, [manageAllSelected, manageSelectedIds, manageTotal]);

  const manageSelectThisPage = (rows: any[]) => {
    const ids = (rows || [])
      .map((r: any) => String(r && r.programmeClubId))
      .filter(id => id && id !== 'undefined' && id !== 'null');

    if (manageAllSelected) {
      setManageDeselectedIds(prev => {
        const next = { ...prev };
        ids.forEach(id => {
          if (next[id]) delete next[id];
        });
        return next;
      });
      return;
    }

    setManageSelectedIds(prev => {
      const next = { ...prev };
      ids.forEach(id => (next[id] = true));
      return next;
    });
  };

  const manageSelectAllMatching = () => {
    setManageAllSelected(true);

    setManageAllSelectedWhere({
      q: (appliedManageSearch || '').trim(),
      onlyDisabled: !!manageOnlyDisabled,
      onlyNoLongerEligible: !!manageOnlyNoLongerEligible
    });

    setManageSelectedIds({});
    setManageDeselectedIds({});
  };

  const manageGetSelectedProgrammeClubIds = React.useCallback((): string[] => {
    if (manageAllSelected) {
      const excluded = new Set(manageGetDeselectedExplicitIds());
      return (manageListFiltered || []).map(r => String(r.programmeClubId)).filter(id => id && !excluded.has(id));
    }
    return manageGetSelectedExplicitIds();
  }, [manageAllSelected, manageDeselectedIds, manageSelectedIds, manageListFiltered]);

  const manageSelectedRows = React.useMemo(() => {
    const ids = new Set(manageGetSelectedProgrammeClubIds().map(String));
    return (manageListFiltered || []).filter(r => ids.has(String(r.programmeClubId)));
  }, [manageGetSelectedProgrammeClubIds, manageListFiltered]);

  const manageSelectedEnabledCount = React.useMemo(() => manageSelectedRows.filter(r => !r.isDisabled).length, [
    manageSelectedRows
  ]);

  const manageSelectedDisabledCount = React.useMemo(() => manageSelectedRows.filter(r => r.isDisabled).length, [
    manageSelectedRows
  ]);

  const onDisableAllNoLongerEligible = async () => {
    if (!canEdit || isBulkDisabling) return;

    const toDisable = (manageListFiltered || [])
      .filter(r => r.isNoLongerEligible && !r.isDisabled)
      .map(r => String(r.programmeClubId))
      .filter(Boolean);

    if (toDisable.length === 0) {
      setManageErr('No enabled “no longer eligible” clubs found.');
      return;
    }

    setManageErr('');
    setIsBulkDisabling(true);
    setBulkProgress({ done: 0, total: toDisable.length });

    try {
      for (let i = 0; i < toDisable.length; i++) {
        const pcId = toDisable[i];
        setSaving(pcId, true);

        try {
          const updated = await updateProgrammeClub(user, programmeId, pcId, { isEnabled: false } as any);
          if (updated) onProgrammeUpdated(updated);
        } catch (e) {
          setManageErr(getErrMsg(e, `Failed to disable some clubs (${i + 1}/${toDisable.length})`));
        } finally {
          setSaving(pcId, false);
          setBulkProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }
      }
    } finally {
      setIsBulkDisabling(false);
      refreshProgrammeClubDetails();
    }
  };

  const onDisableSelected = async () => {
    if (!canEdit || isBulkDisabling) return;

    const selected = manageGetSelectedProgrammeClubIds();

    if (!selected.length) {
      setManageErr('No clubs selected.');
      return;
    }

    const selectedRowsLocal = manageListFiltered.filter(r => selected.includes(String(r.programmeClubId)));
    const toDisable = selectedRowsLocal
      .filter(r => !r.isDisabled)
      .map(r => String(r.programmeClubId))
      .filter(Boolean);

    if (!toDisable.length) {
      setManageErr('Selected clubs are already disabled.');
      return;
    }

    setManageErr('');
    setIsBulkDisabling(true);
    setBulkProgress({ done: 0, total: toDisable.length });

    try {
      for (let i = 0; i < toDisable.length; i++) {
        const pcId = toDisable[i];
        setSaving(pcId, true);

        try {
          const updated = await updateProgrammeClub(user, programmeId, pcId, { isEnabled: false } as any);
          if (updated) onProgrammeUpdated(updated);
        } catch (e) {
          setManageErr(getErrMsg(e, `Failed to disable some clubs (${i + 1}/${toDisable.length})`));
        } finally {
          setSaving(pcId, false);
          setBulkProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }
      }
    } finally {
      setIsBulkDisabling(false);
      refreshProgrammeClubDetails();
      manageClearSelection();
    }
  };

  const onRemoveSelectedConfirmed = async () => {
    if (!canEdit || isBulkDisabling) return;

    const selected = manageGetSelectedProgrammeClubIds();
    if (!selected.length) {
      setManageErr('No clubs selected.');
      return;
    }

    const selectedRowsLocal = manageListFiltered.filter(r => selected.includes(String(r.programmeClubId)));

    const toRemove = selectedRowsLocal
      .filter(r => r.isDisabled)
      .map(r => String(r.programmeClubId))
      .filter(Boolean);

    if (!toRemove.length) {
      setManageErr('Nothing to remove.');
      return;
    }

    setManageErr('');
    setIsBulkDisabling(true);
    setBulkProgress({ done: 0, total: toRemove.length });

    try {
      for (let i = 0; i < toRemove.length; i++) {
        const pcId = toRemove[i];
        setSaving(pcId, true);

        try {
          const updated = await deleteProgrammeClub(user, programmeId, pcId);
          if (updated) onProgrammeUpdated(updated);
        } catch (e) {
          setManageErr(getErrMsg(e, `Failed to remove some clubs (${i + 1}/${toRemove.length})`));
        } finally {
          setSaving(pcId, false);
          setBulkProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }
      }
    } finally {
      setIsBulkDisabling(false);
      refreshProgrammeClubDetails();
      manageClearSelection();
      setIsRemoveSelectedOpen(false);
    }
  };
  const manageFrom = manageTotal === 0 ? 0 : (manageSafePage - 1) * LIMIT + 1;
  const manageTo = Math.min(manageTotal, manageSafePage * LIMIT);

  const manageExcludedCount = Object.keys(manageDeselectedIds).filter(k => manageDeselectedIds[k]).length;
  const manageIsPartialAll = manageAllSelected && manageExcludedCount > 0;
  const hiddenManageColumns = React.useMemo(() => new Set(hiddenManageColumnKeys), [hiddenManageColumnKeys]);
  const visibleManageColumns = React.useMemo(
    () =>
      MANAGE_CLUB_COLUMNS.filter(
        column => MANAGE_CLUB_LOCKED_COLUMNS.has(column.field) || !hiddenManageColumns.has(column.field)
      ),
    [hiddenManageColumns]
  );
  const visibleManageColumnsCount = visibleManageColumns.length;
  const toggleManageColumn = (field: string) => {
    if (MANAGE_CLUB_LOCKED_COLUMNS.has(field)) return;
    setHiddenManageColumnKeys(prev => (prev.includes(field) ? prev.filter(x => x !== field) : [...prev, field]));
  };
  const resetManageColumns = () => setHiddenManageColumnKeys([]);

  const pageWindowStart = Math.max(1, manageSafePage - 2);
  const pageWindowEnd = Math.min(managePages, manageSafePage + 2);
  const managePageNumbers: number[] = [];
  for (let n = pageWindowStart; n <= pageWindowEnd; n += 1) managePageNumbers.push(n);

  const statusLabel = String((programme as any).status || 'DRAFT').toLowerCase() === 'active' ? 'Active' : 'Draft';
  const selectedCountLabel = `${manageSelectedCount} selected`;
  const activeManageFilterChips = [
    ...(appliedManageSearch.trim() ? [{ key: 'search', label: `Search: ${appliedManageSearch.trim()}` }] : []),
    ...(manageOnlyDisabled ? [{ key: 'disabled', label: 'Disabled clubs only' }] : []),
    ...(manageOnlyNoLongerEligible ? [{ key: 'ineligible', label: 'No longer eligible only' }] : [])
  ];

  const openManageFilters = () => {
    setManageOnlyDisabledDraft(manageOnlyDisabled);
    setManageOnlyNoLongerEligibleDraft(manageOnlyNoLongerEligible);
    setIsManageColumnsOpen(false);
    setIsManageFiltersOpen(true);
  };

  const closeManageFilters = () => setIsManageFiltersOpen(false);

  const applyManageFilters = () => {
    setManageOnlyDisabled(manageOnlyDisabledDraft);
    setManageOnlyNoLongerEligible(manageOnlyNoLongerEligibleDraft);
    setIsManageFiltersOpen(false);
  };

  const clearManageFilters = () => {
    setManageSearch('');
    setAppliedManageSearch('');
    setManageOnlyDisabled(false);
    setManageOnlyNoLongerEligible(false);
    setManageOnlyDisabledDraft(false);
    setManageOnlyNoLongerEligibleDraft(false);
    setIsManageFiltersOpen(false);
  };

  const removeManageFilter = (key: string) => {
    if (key === 'search') {
      setManageSearch('');
      setAppliedManageSearch('');
    }
    if (key === 'disabled') {
      setManageOnlyDisabled(false);
      setManageOnlyDisabledDraft(false);
    }
    if (key === 'ineligible') {
      setManageOnlyNoLongerEligible(false);
      setManageOnlyNoLongerEligibleDraft(false);
    }
  };

  const requestRemoveRow = (row: any) => {
    const rowId = String(row?.programmeClubId || '');
    if (!rowId || row?.isDisabled !== true) return;
    setManageAllSelected(false);
    setManageSelectedIds({ [rowId]: true });
    setManageDeselectedIds({});
    setManageAllSelectedWhere(null);
    setSelectedProgrammeClubIds([rowId]);
    setIsRemoveSelectedOpen(true);
  };

  const renderDrawerContent = () => {
    if (!selectedRow) return null;

    const tab = currentTabIndex === 1 ? 'fields' : currentTabIndex === 2 ? 'invites' : 'summary';
    const pcId = String(selectedRow.programmeClubId || '');

    if (tab === 'fields') {
      const bookingForm = getBookingFormPreview(selectedRow);
      const isSavingCurrent = isBulkDisabling || !!savingIds[pcId];
      const renderSegment = (mode: 'DEFAULT' | 'CUSTOM', label: string, hint: string) => {
        const active = mode === 'CUSTOM' ? bookingForm.hasOverride : !bookingForm.hasOverride;
        return (
          <button
            type="button"
            className={active ? 'pcSegment pcSegment--active' : 'pcSegment'}
            disabled={!canEdit || isSavingCurrent}
            onClick={() => {
              if (active) return;
              onSetBookingFormMode(pcId, mode);
            }}
          >
            <span className="pcSegmentHead">
              <i />
              <b>{label}</b>
            </span>
            <small>{hint}</small>
          </button>
        );
      };

      return (
        <div className="pcDrawerPanel pcBookingFormTab">
          <div className="pcDrawerKicker">Booking form for this club</div>
          <div className="pcSegmentGroup">
            {renderSegment('DEFAULT', 'Programme default', "Use the programme's booking form.")}
            {renderSegment('CUSTOM', 'Custom for this club', 'Override with a club-specific form.')}
          </div>

          {bookingForm.hasOverride && !bookingForm.customNotSetup ? (
            <div className="pcBookingFormHeader">
              <div>{bookingForm.sourceLabel}</div>
              {canEdit ? (
                <button type="button" className="pcGhostMiniButton" onClick={() => openFieldsOverride(selectedRow)}>
                  <ManageClubsIcon icon="swap" size={14} />
                  <span>Change</span>
                </button>
              ) : null}
            </div>
          ) : null}

          {bookingForm.customNotSetup ? (
            <div className="pcFormEmptyState">
              <span className="pcFormEmptyIcon">
                <ManageClubsIcon icon="list" size={20} strokeWidth={1.6} />
              </span>
              <div className="pcFormEmptyTitle">No custom form yet</div>
              <div className="pcFormEmptyText">
                Start from a Form-builder template or build one from scratch for this club.
              </div>
              {canEdit ? (
                <div className="pcFormEmptyActions">
                  <button
                    type="button"
                    className="pcMiniButton pcMiniButton--secondary"
                    onClick={() => openFieldsOverride(selectedRow)}
                  >
                    Use a template
                  </button>
                  <button
                    type="button"
                    className="pcMiniButton pcMiniButton--ghost"
                    onClick={() => openFieldsOverride(selectedRow)}
                  >
                    Build from scratch
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className="pcMetricStrip pcBookingMetrics">
                <span>
                  <b>{bookingForm.questions}</b> questions
                </span>
                <span>
                  <b>{bookingForm.required}</b> required
                </span>
                <span>
                  <b>{bookingForm.pages}</b> {bookingForm.pages === 1 ? 'page' : 'pages'}
                </span>
              </div>
              <div className="pcFormElementList">
                {bookingForm.elements.length ? (
                  bookingForm.elements.map((element: any, index: number) => {
                    const typeDef = getFormFieldTypeDefinition(element?.type as any);
                    const key = String(element?.id || `${element?.type || 'element'}-${index}`);
                    if (element?.type === 'PAGE_BREAK') {
                      return (
                        <div className="pcFormPageBreak" key={key}>
                          <span />
                          <b>Page break</b>
                          <span />
                        </div>
                      );
                    }
                    if (element?.kind !== 'QUESTION') {
                      return (
                        <div className="pcFormContentRow" key={key}>
                          {String(element?.title || typeDef?.label || 'Content')}
                        </div>
                      );
                    }
                    return (
                      <div className="pcFormQuestionRow" key={key}>
                        <span className="pcFormQuestionIndex" aria-hidden="true">
                          {
                            bookingForm.elements.slice(0, index + 1).filter((item: any) => item?.kind === 'QUESTION')
                              .length
                          }
                        </span>
                        <span className="pcFormQuestionTitle">
                          {String(element?.title || typeDef?.label || 'Question')}
                        </span>
                        <em>{typeDef?.label || String(element?.type || 'Question')}</em>
                        {element?.required || element?.isRequired ? <i>Required</i> : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="pcDrawerMutedNote">No questions have been added to this booking form.</div>
                )}
              </div>
            </>
          )}

          {!bookingForm.hasOverride ? (
            <div className="pcDrawerMutedNote">
              This is the form set on the programme's Fields tab. Switch to Custom to give this club its own form.
            </div>
          ) : null}
        </div>
      );
    }
    if (tab === 'invites') {
      return (
        <div className="pcDrawerPanel">
          <div className="pcInviteSwitchRow">
            <div>
              <div className="pcDrawerStrong">Invites only</div>
              <div className="pcDrawerHint">Restrict this club to invited students only.</div>
            </div>
            <input
              type="checkbox"
              className="pcSwitch"
              checked={!!selectedRow.invitesOnly}
              disabled={!canEdit || isBulkDisabling || !!savingIds[pcId]}
              onChange={e => onToggleInvitesOnly(pcId, !!e.target.checked)}
            />
          </div>
          <div className="pcMetricGrid">
            <div>
              <small>Eligible</small>
              <b>{clubStudentsCountByClubId[selectedRow.clubId] ?? 0}</b>
            </div>
            <div>
              <small>Invited</small>
              <b>{selectedRow.invitedCount || 0}</b>
            </div>
          </div>
          {!selectedRow.invitesOnly ? (
            <div className="pcDrawerInfoNote">
              This club is open to all eligible students. The invite list does not restrict visibility until Invites
              only is enabled.
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="pcDrawerPanel">
        <div className="pcRunCard">
          <span className="pcRunIcon">
            <ManageClubsIcon icon="calendar" size={20} />
          </span>
          <div>
            <div className="pcRunLabel">Runs</div>
            <div className="pcRunValue">
              {selectedRow.day || '—'} · {selectedRow.sessionTime || selectedRow.time || '—'}
            </div>
          </div>
        </div>
        <div className="pcDrawerFieldList">
          <div className="pcDrawerFieldRow">
            <span>Description</span>
            <b>{String(propz.get(selectedRow.club, ['description'], '') || '—')}</b>
          </div>
          <div className="pcDrawerFieldRow">
            <span>Dates</span>
            <b>{selectedRow.dateRange || selectedRow.startDate || '—'}</b>
          </div>
          <div className="pcDrawerFieldRow">
            <span>Audience</span>
            <b>
              {selectedRow.gender || '—'} · {selectedRow.ages || '—'}
            </b>
          </div>
          <div className="pcDrawerFieldRow">
            <span>Places</span>
            <b>{selectedRow.placesLabel || 'Unlimited'}</b>
          </div>
          <div className="pcDrawerFieldRow">
            <span>Coach / Leader</span>
            <b>{String(propz.get(selectedRow.club, ['coach'], '') || '—')}</b>
          </div>
          <div className="pcDrawerFieldRow">
            <span>Member of staff</span>
            <b>{String(propz.get(selectedRow.club, ['memberOfStaff'], '') || '—')}</b>
          </div>
        </div>
        {selectedRow.isNoLongerEligible ? (
          <div className="pcDrawerWarnNote">This club is no longer eligible (finished or in the past).</div>
        ) : null}
      </div>
    );
  };

  const renderDrawerFooter = () => {
    if (!selectedRow) return null;
    const pcId = String(selectedRow.programmeClubId || '');
    if (currentTabIndex === 1) {
      if (!selectedRow.pc?.fieldsOverride) return null;
      const bookingForm = getBookingFormPreview(selectedRow);
      return (
        <button
          type="button"
          className="pcDrawerButton pcDrawerButton--primary"
          disabled={!canEdit || isBulkDisabling || !!savingIds[pcId]}
          onClick={() => openFieldsOverride(selectedRow)}
        >
          <span className="pcDrawerButtonIcon">
            <ManageClubsIcon icon={bookingForm.customNotSetup ? 'plus' : 'edit'} size={16} />
          </span>
          <span>{bookingForm.customNotSetup ? 'Set up form' : 'Edit booking form'}</span>
        </button>
      );
    }
    if (currentTabIndex === 2) {
      return (
        <button
          type="button"
          className="pcDrawerButton pcDrawerButton--primary"
          disabled={!canEdit || !selectedRow.invitesOnly}
          onClick={() => openInviteList(selectedRow)}
        >
          <span className="pcDrawerButtonIcon">
            <ManageClubsIcon icon="mail" size={16} />
          </span>
          <span>Open invite picker</span>
        </button>
      );
    }
    return (
      <button
        type="button"
        className="pcDrawerButton pcDrawerButton--secondary"
        disabled={!canEdit || isBulkDisabling || !!savingIds[pcId]}
        onClick={() => onToggleEnabled(pcId, selectedRow.isDisabled)}
      >
        <span className="pcDrawerButtonIcon">
          <ManageClubsIcon icon="lock" size={16} />
        </span>
        <span>{selectedRow.isDisabled ? 'Enable club' : 'Disable club'}</span>
      </button>
    );
  };

  return (
    <div className="pc-scroll pcProgrammeClubsPage">
      <div className="pcProgrammeClubsHeader">
        <button type="button" className="pcBackLink" onClick={onBackClick}>
          <ManageClubsIcon icon="back" size={15} />
          <span>Back to activity programmes</span>
        </button>
        <div className="pcProgrammeClubsHeaderMain">
          <div>
            <div className="pcProgrammeClubsTitleRow">
              <h1 className="pcProgrammeClubsTitle">{programmeName}</h1>
              <span className={`pcBadge ${statusLabel === 'Active' ? 'pcBadge--success' : 'pcBadge--warning'}`}>
                {statusLabel}
              </span>
            </div>
            <div className="pcProgrammeClubsSubtitle">
              Add clubs to the programme and control their booking forms and invite lists.
            </div>
          </div>
          <button
            type="button"
            className="pcButton pcButton--primary"
            disabled={!canEdit}
            onClick={() => setIsAddClubsOpen(true)}
          >
            <ManageClubsIcon icon="plus" size={16} strokeWidth={2} />
            <span>Add clubs</span>
          </button>
        </div>
      </div>

      <div className="pcProgrammeClubsBody">
        <div className="pcToolbar pcToolbar--inline">
          <div className="pcToolbarTitleWrap">
            <div className="pcToolbarTitle">Clubs in this programme</div>
            <span className="pcToolbarCountChip">{manageTotal}</span>
          </div>
          <div className="pcSearchBox pcSearchBox--inline">
            <span className="pcSearchIcon">
              <ManageClubsIcon icon="search" size={15} />
            </span>
            <input
              value={manageSearch}
              aria-label="Search clubs"
              placeholder="Search by club or activity…"
              onChange={e => setManageSearch(e.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setAppliedManageSearch(manageSearch);
                }
              }}
            />
            {manageSearch ? (
              <button
                type="button"
                className="pcSearchClear"
                onClick={() => {
                  setManageSearch('');
                  setAppliedManageSearch('');
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="cpDsFilterControl pcManageFilterControl">
            {isManageFiltersOpen ? (
              <button
                type="button"
                className="cpDsFilterScrim"
                aria-label="Dismiss filters"
                onClick={closeManageFilters}
              />
            ) : null}
            <button
              type="button"
              className={`pcButton pcButton--secondary ${isManageFiltersOpen ? 'isActive' : ''}`}
              onClick={() => (isManageFiltersOpen ? closeManageFilters() : openManageFilters())}
              aria-expanded={isManageFiltersOpen}
            >
              <ManageClubsIcon icon="filter" size={17} strokeWidth={2} />
              <span>Filters</span>
              {activeManageFilterChips.length ? (
                <span className="cpDsFilterCount">{activeManageFilterChips.length}</span>
              ) : null}
            </button>
            {isManageFiltersOpen ? (
              <aside
                className="cpDsFilterDrawer sit-drawer sit-drawer--right pcManageFilterDrawer"
                role="dialog"
                aria-modal="true"
                aria-label="Club filters"
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyManageFilters();
                  }
                }}
              >
                <header>
                  <strong>Filters</strong>
                  <button type="button" onClick={closeManageFilters} aria-label="Close filters">
                    ×
                  </button>
                </header>
                <div className="cpDsFilterFields">
                  <div className="cpDsFilterField cpDsFilterFieldWide">
                    <span>Status</span>
                    <div className="cpDsFilterChips" role="group" aria-label="Club status">
                      <button
                        type="button"
                        className={`cpDsFilterChip ${manageOnlyDisabledDraft ? 'isSelected' : ''}`}
                        onClick={() => setManageOnlyDisabledDraft(value => !value)}
                      >
                        Disabled clubs
                      </button>
                    </div>
                  </div>
                  <div className="cpDsFilterField cpDsFilterFieldWide">
                    <span>Eligibility</span>
                    <div className="cpDsFilterChips" role="group" aria-label="Club eligibility">
                      <button
                        type="button"
                        className={`cpDsFilterChip ${manageOnlyNoLongerEligibleDraft ? 'isSelected' : ''}`}
                        onClick={() => setManageOnlyNoLongerEligibleDraft(value => !value)}
                      >
                        No longer eligible
                      </button>
                    </div>
                  </div>
                </div>
                <footer>
                  <button type="button" className="isClear" onClick={clearManageFilters}>
                    Clear all
                  </button>
                  <div>
                    <button type="button" onClick={closeManageFilters}>
                      Cancel
                    </button>
                    <button type="button" className="isApply" onClick={applyManageFilters}>
                      Apply
                    </button>
                  </div>
                </footer>
              </aside>
            ) : null}
          </div>
          <div className={`pcColumnsMenuWrap ${isManageColumnsOpen ? 'isOpen' : ''}`} ref={manageColumnsRef}>
            {isManageColumnsOpen ? (
              <button
                type="button"
                className="pcColumnsScrim"
                aria-label="Close column settings"
                onClick={() => setIsManageColumnsOpen(false)}
              />
            ) : null}
            <button
              type="button"
              className="pcButton pcButton--secondary pcButton--columns"
              onClick={() => {
                setIsManageFiltersOpen(false);
                setIsManageColumnsOpen(value => !value);
              }}
              aria-expanded={isManageColumnsOpen}
            >
              <ManageClubsIcon icon="columns" size={16} strokeWidth={1.9} />
              <span>Columns</span>
              <span className="pcButtonBadge">{visibleManageColumnsCount}</span>
            </button>
            {isManageColumnsOpen ? (
              <div className="pcColumnsMenu">
                <div className="pcColumnsMenuHead">
                  <span>Show / hide columns</span>
                  <button type="button" onClick={resetManageColumns}>
                    Reset
                  </button>
                </div>
                {MANAGE_CLUB_COLUMNS.map(column => {
                  const isLocked = MANAGE_CLUB_LOCKED_COLUMNS.has(column.field);
                  const isVisible = isLocked || !hiddenManageColumns.has(column.field);
                  return (
                    <label
                      key={column.field}
                      className={isLocked ? 'pcColumnsMenuItem pcColumnsMenuItem--locked' : 'pcColumnsMenuItem'}
                    >
                      <input
                        type="checkbox"
                        checked={isVisible}
                        disabled={isLocked}
                        onChange={() => toggleManageColumn(column.field)}
                      />
                      <span>{column.text}</span>
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
          {!canEdit ? (
            <div className="pcReadonly">
              <span className="pcReadonlyIcon">
                <ManageClubsIcon icon="lock" size={16} />
              </span>
              Read-only while active
            </div>
          ) : null}
        </div>

        {activeManageFilterChips.length ? (
          <div className="pcManageActiveFilters">
            {activeManageFilterChips.map(chip => (
              <button type="button" key={chip.key} onClick={() => removeManageFilter(chip.key)}>
                <span>{chip.label}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
            <button type="button" className="pcManageClearFilters" onClick={clearManageFilters}>
              Clear all
            </button>
          </div>
        ) : null}

        {manageHasAnySelection ? (
          <div className="pcSelectionBar">
            <div>{selectedCountLabel}</div>
            <div className="pcSelectionActions">
              <button
                type="button"
                className="pcButton pcButton--secondary"
                disabled={!canEdit || isBulkDisabling}
                onClick={onDisableSelected}
              >
                Disable
              </button>
              <button
                type="button"
                className="pcButton pcButton--danger"
                disabled={!canEdit || isBulkDisabling || manageSelectedDisabledCount === 0}
                onClick={() => setIsRemoveSelectedOpen(true)}
              >
                Remove selected
              </button>
              <button
                type="button"
                className="pcButton pcButton--ghost"
                disabled={isBulkDisabling}
                onClick={manageClearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        ) : null}

        {manageErr ? <div className="pcErrorText">{manageErr}</div> : null}

        <div className="pcCard pcCard--flush">
          {isProgrammeClubsLoading ? (
            <div className="pcProgrammeClubsLoading">Loading club details...</div>
          ) : (
            <ClubProgrammeManageClubsTable
              rows={manageVisible}
              columns={visibleManageColumns}
              selectedProgrammeClubs={manageSelectedRows}
              canEdit={canEdit}
              isBulkDisabling={isBulkDisabling}
              savingIds={savingIds}
              onToggleRowSelection={(row: any) => {
                const rowId = String(row?.programmeClubId || '');
                if (!rowId) return;

                setSelectedProgrammeClubIds(prev => {
                  const exists = (prev || []).some(x => String(x) === rowId);
                  return exists ? prev.filter(x => String(x) !== rowId) : [...prev, rowId];
                });

                setManageAllSelected(false);
                setManageDeselectedIds({});
                setManageAllSelectedWhere(null);

                setManageSelectedIds(prev => {
                  const next = { ...prev };
                  if (next[rowId]) delete next[rowId];
                  else next[rowId] = true;
                  return next;
                });
              }}
              onRowClick={(row: any) => {
                setPreviewProgrammeClubId(String(row?.programmeClubId || ''));
                setCurrentTabIndex(0);
              }}
              onToggleEnabled={onToggleEnabled}
              onOpenBookingForm={(row: any) => {
                setPreviewProgrammeClubId(String(row?.programmeClubId || ''));
                setCurrentTabIndex(1);
              }}
              onOpenInvites={(row: any) => openInviteList(row)}
              onRequestRemove={requestRemoveRow}
            />
          )}
        </div>

        <div className="pcPagination pcPagination--pages">
          <span className="pcPaginationSummary">
            Page {manageSafePage} of {managePages}
          </span>
          <button
            type="button"
            className="pcButton pcButton--secondary"
            disabled={manageSafePage <= 1}
            onClick={() => setManagePage(p => Math.max(1, p - 1))}
          >
            Previous
          </button>
          {pageWindowStart > 1 ? (
            <React.Fragment>
              <button type="button" className="pcPageBtn" onClick={() => setManagePage(1)}>
                1
              </button>
              {pageWindowStart > 2 ? <span className="pcPaginationEllipsis">...</span> : null}
            </React.Fragment>
          ) : null}
          {managePageNumbers.map(pageNumber => (
            <button
              key={pageNumber}
              type="button"
              className={pageNumber === manageSafePage ? 'pcPageBtn pcPageBtn--active' : 'pcPageBtn'}
              onClick={() => setManagePage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}
          {pageWindowEnd < managePages ? (
            <React.Fragment>
              {pageWindowEnd < managePages - 1 ? <span className="pcPaginationEllipsis">...</span> : null}
              <button type="button" className="pcPageBtn" onClick={() => setManagePage(managePages)}>
                {managePages}
              </button>
            </React.Fragment>
          ) : null}
          <button
            type="button"
            className="pcButton pcButton--secondary"
            disabled={manageSafePage >= managePages}
            onClick={() => setManagePage(p => Math.min(managePages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {selectedRow ? (
        <div className="pcDrawerOverlay" onClick={() => setPreviewProgrammeClubId('')}>
          <div
            className="pcDrawerSheet"
            role="dialog"
            aria-modal="true"
            aria-label="Club details"
            onClick={e => e.stopPropagation()}
          >
            <div className="pcDrawerHead">
              <span className="pcDrawerLead">
                <ManageClubsIcon icon="whistle" size={22} />
              </span>
              <div className="pcDrawerTitles">
                <div className="pcDrawerTitle">{selectedRow.name || 'Club'}</div>
                <div className="pcDrawerMeta">
                  <span
                    className={
                      selectedRow.isDisabled
                        ? 'pcBadge pcBadge--neutral pcBadge--dot'
                        : 'pcBadge pcBadge--success pcBadge--dot'
                    }
                  >
                    {selectedRow.isDisabled ? 'Disabled' : 'Enabled'}
                  </span>
                  <i />
                  <span>{selectedRow.sportName || '—'}</span>
                  <i />
                  <span>
                    {selectedRow.day || '—'} {selectedRow.sessionTime || selectedRow.time || ''}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="pcDrawerClose"
                aria-label="Close club details"
                onClick={() => setPreviewProgrammeClubId('')}
              >
                ×
              </button>
            </div>
            <div className="pcDrawerTabs">
              {['Summary', 'Booking form', 'Invites'].map((tab, index) => (
                <button
                  key={tab}
                  type="button"
                  className={currentTabIndex === index ? 'isActive' : ''}
                  onClick={event => onTabClick(event, index)}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="pcDrawerBody">{renderDrawerContent()}</div>
            {renderDrawerFooter() ? <div className="pcDrawerFoot">{renderDrawerFooter()}</div> : null}
          </div>
        </div>
      ) : null}

      <ClubProgrammeAddClubsModal
        isOpen={isAddClubsOpen}
        user={user}
        programme={programme}
        onClose={() => setIsAddClubsOpen(false)}
        onProgrammeUpdated={onAddClubsProgrammeUpdated}
      />

      <ClubProgrammeManageClubsInviteModal
        isOpen={isInviteListOpen}
        inviteInvitesOnly={inviteInvitesOnly}
        inviteErr={inviteErr}
        inviteEligibleLoading={inviteEligibleLoading}
        onClose={closeInviteList}
        onSave={onInviteListSave}
        inviteEligibleItems={inviteEligibleItems}
        inviteEligibleSelected={inviteEligibleSelected}
        setInviteEligibleSelected={setInviteEligibleSelected}
        inviteInvitedItems={inviteInvitedItems}
        inviteInvitedItemsSorted={inviteInvitedItemsSorted}
        inviteInvitedSelected={inviteInvitedSelected}
        setInviteInvitedSelected={setInviteInvitedSelected}
        inviteFiltersDraft={inviteFiltersDraft}
        invitePresetFilters={invitePresetFilters}
        setInviteFiltersDraft={setInviteFiltersDraft}
        inviteItemsFiltersOptions={inviteItemsFiltersOptions}
        inviteShowEligibleFilter={inviteShowEligibleFilter}
        setInviteShowEligibleFilter={setInviteShowEligibleFilter}
        inviteTotal={inviteTotal}
        invitePage={invitePage}
        loadInvitePage={loadInvitePage}
        setInviteFiltersApplied={setInviteFiltersApplied}
        replaceInviteFilters={replaceInviteFilters}
        inviteSearch={inviteSearch}
        setInviteSearch={setInviteSearch}
        setInvitePage={setInvitePage}
        INVITE_FILTERS_EMPTY={INVITE_FILTERS_EMPTY}
        INVITE_ITEMS_COLUMNS={INVITE_ITEMS_COLUMNS}
        onAddClick={onInviteAddClick}
        onRemoveClick={onInviteRemoveClick}
        onAddSingleItem={onInviteAddSingleItem}
        onRemoveSingleItem={onInviteRemoveSingleItem}
        inviteItemsAddBuffer={inviteItemsAddBuffer}
        inviteItemsRemoveBuffer={inviteItemsRemoveBuffer}
        contextTitle={`${programmeName} - ${inviteClubName} - Invite list`}
        loadAllInviteEligible={loadAllInviteEligible}
        setInviteEligibleLoading={setInviteEligibleLoading}
        inviteInitialInvitedIds={inviteInitialInvitedIds}
        inviteInvitedLoading={inviteInvitedLoading}
      />

      <ClubProgrammeManageClubsRemoveSelectedModal
        isOpen={isRemoveSelectedOpen}
        isBulkDisabling={isBulkDisabling}
        manageSelectedCount={manageSelectedCount}
        manageSelectedDisabledCount={manageSelectedDisabledCount}
        manageSelectedEnabledCount={manageSelectedEnabledCount}
        bulkProgress={bulkProgress}
        onClose={() => setIsRemoveSelectedOpen(false)}
        onConfirm={onRemoveSelectedConfirmed}
      />
    </div>
  );
};
